import React, { useMemo, useState } from "react";

import {
    FaCheckCircle,
    FaExclamationTriangle,
    FaBalanceScale,
    FaChevronDown,
    FaChevronUp
} from "react-icons/fa";


function LedgerCrossCheck({
    partyData = {},
    partyTypes = []
}) {

    const [expandedParty, setExpandedParty] =
        useState(null);


    // =========================================================
    // FORMAT NUMBER
    // =========================================================

    const formatAmount = (amount) => {

        const value =
            Number(amount) || 0;

        return value.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });

    };


    // =========================================================
    // FORMAT CURRENCY
    // =========================================================

    const formatCurrency = (
        amount,
        currency = "INR"
    ) => {

        const value =
            Number(amount) || 0;


        if (currency === "USD") {

            return `$${value.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`;

        }


        if (currency === "BRL") {

            return `R$ ${value.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`;

        }


        return `₹${value.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;

    };


    // =========================================================
    // PARTY NAME
    // =========================================================

    const getPartyName = (party) => {

        return (
            party.name ||
            party.party_name ||
            party.company_name ||
            party.bank_name ||
            party.supplier_name ||
            party.buyer_name ||
            party.title ||
            "N/A"
        );

    };


    // =========================================================
    // CREATE FLAT PARTY ROWS
    // =========================================================

    const rows = useMemo(() => {

        const result = [];


        partyTypes.forEach((partyType) => {

            const parties =
                partyData[partyType.key] || [];


            if (!Array.isArray(parties)) {
                return;
            }
			

            parties.forEach((party) => {
                console.log(party);
                result.push({

                    ...party,

                    partyKey:
                        partyType.key,

                    partyTitle:
                        partyType.title,

                    partyIcon:
                        partyType.icon,

                    partyId:
                        party._id ||
                        party.party_id

                });

            });

        });


        return result;

    }, [partyData, partyTypes]);


    // =========================================================
    // TOGGLE PARTY
    // =========================================================

    const toggleParty = (
        partyKey,
        partyId
    ) => {

        const id =
            `${partyKey}-${partyId}`;


        if (expandedParty === id) {

            setExpandedParty(null);

        } else {

            setExpandedParty(id);

        }

    };


    // =========================================================
    // NORMAL PARTY TOTAL
    // =========================================================

    const getNormalDifference = (party) => {

        const opening =
            Number(
                party.opening_balance
            ) || 0;


        const ledger =
            Number(
                party.total_amount
            ) || 0;


        return opening + ledger;

    };


    // =========================================================
    // OVERALL TOTALS
    // =========================================================

    const totals = useMemo(() => {

        let totalParties = 0;

        let totalTransactions = 0;

        let totalINR = 0;

        let totalOpening = 0;


        rows.forEach((party) => {

            totalParties++;


            totalTransactions +=
                Number(
                    party.ledger_count
                ) || 0;


            /*
             * Banks contain USD/BRL.
             * Do not add bank currency values
             * into INR total.
             */

            if (
                party.partyKey !== "banks"
            ) {

                totalOpening +=
                    Number(
                        party.opening_balance
                    ) || 0;


                totalINR +=
                    Number(
                        party.total_amount
                    ) || 0;

            }

        });


        return {

            totalParties,

            totalTransactions,

            totalOpening,

            totalINR

        };

    }, [rows]);


    // =========================================================
    // STATUS
    // =========================================================

    const getStatus = (
        difference
    ) => {

        const value =
            Number(difference) || 0;


        if (
            Math.abs(value) < 0.01
        ) {

            return {

                text: "Matched",

                className:
                    "text-success",

                icon:
                    <FaCheckCircle />

            };

        }


        return {

            text: "Mismatch",

            className:
                "text-danger",

            icon:
                <FaExclamationTriangle />

        };

    };


    // =========================================================
    // EMPTY STATE
    // =========================================================

    if (!rows.length) {

        return (

            <div className="card shadow-sm border-0">

                <div className="card-body text-center py-5">

                    <FaBalanceScale
                        className="text-muted mb-3"
                        size={35}
                    />

                    <h5 className="text-muted">

                        No party data available

                    </h5>

                    <p className="text-muted mb-0">

                        Party information will appear here
                        after the Summary API is loaded.

                    </p>

                </div>

            </div>

        );

    }


    // =========================================================
    // MAIN UI
    // =========================================================

    return (

        <div className="container-fluid px-0">


            {/* =================================================
                HEADER
            ================================================= */}

            <div className="card shadow-sm border-0 mb-3">

                <div className="card-body">

                    <div className="d-flex justify-content-between align-items-center">

                        <div>

                            <h4 className="mb-1 fw-bold">

                                <FaBalanceScale
                                    className="me-2"
                                />

                                All Parties Ledger

                            </h4>

                            <small className="text-muted">

                                Complete party-wise ledger
                                summary from API

                            </small>

                        </div>


                        <div className="text-end">

                            <div className="fw-bold">

                                {totals.totalParties}

                            </div>

                            <small className="text-muted">

                                Parties

                            </small>

                        </div>

                    </div>

                </div>

            </div>


            {/* =================================================
                SUMMARY CARDS
            ================================================= */}

            <div className="row g-3 mb-3">


                {/* TOTAL PARTIES */}

                <div className="col-xl-3 col-md-6">

                    <div className="card border-0 shadow-sm">

                        <div className="card-body">

                            <small className="text-muted">

                                Total Parties

                            </small>

                            <h3 className="fw-bold mb-0">

                                {totals.totalParties.toLocaleString(
                                    "en-IN"
                                )}

                            </h3>

                        </div>

                    </div>

                </div>


                {/* TRANSACTIONS */}

                <div className="col-xl-3 col-md-6">

                    <div className="card border-0 shadow-sm">

                        <div className="card-body">

                            <small className="text-muted">

                                Total Transactions

                            </small>

                            <h3 className="fw-bold mb-0">

                                {totals.totalTransactions.toLocaleString(
                                    "en-IN"
                                )}

                            </h3>

                        </div>

                    </div>

                </div>


                {/* OPENING */}

                <div className="col-xl-3 col-md-6">

                    <div className="card border-0 shadow-sm">

                        <div className="card-body">

                            <small className="text-muted">

                                Total Opening Balance

                            </small>

                            <h3 className="fw-bold mb-0">

                                {formatCurrency(
                                    totals.totalOpening
                                )}

                            </h3>

                        </div>

                    </div>

                </div>


                {/* LEDGER */}

                <div className="col-xl-3 col-md-6">

                    <div className="card border-0 shadow-sm">

                        <div className="card-body">

                            <small className="text-muted">

                                Total Ledger Amount

                            </small>

                            <h3 className="fw-bold mb-0">

                                {formatCurrency(
                                    totals.totalINR
                                )}

                            </h3>

                        </div>

                    </div>

                </div>

            </div>


            {/* =================================================
                PARTY TABLE
            ================================================= */}

            <div className="card shadow-sm border-0">


                <div className="card-header bg-white">

                    <strong>

                        All Party Details

                    </strong>

                </div>


                <div className="table-responsive">

                    <table className="table table-bordered table-hover align-middle mb-0">

                        <thead className="table-light">

                            <tr>

                                <th
                                    style={{
                                        width: "50px"
                                    }}
                                >
                                    #
                                </th>

                                <th>
                                    Type
                                </th>

                                <th>
                                    Party
                                </th>

                                <th className="text-end">
                                    Opening
                                </th>

                                <th className="text-end">
                                    Ledger Amount
                                </th>

                                <th className="text-end">
                                    Transactions
                                </th>

                                <th className="text-end">
                                    Balance
                                </th>

                                <th
                                    className="text-center"
                                    style={{
                                        width: "100px"
                                    }}
                                >
                                    Action
                                </th>

                            </tr>

                        </thead>


                        <tbody>

                            {rows.map(
                                (party, index) => {

                                    const partyId =
                                        party.partyId;


                                    const rowId =
                                        `${party.partyKey}-${partyId}`;


                                    const isExpanded =
                                        expandedParty === rowId;


                                    const isBank =
                                        party.partyKey ===
                                        "banks";


                                    const balance =
                                        getNormalDifference(
                                            party
                                        );


                                    const status =
                                        getStatus(
                                            balance
                                        );


                                    return (

                                        <React.Fragment
                                            key={rowId}
                                        >


                                            {/* ==================================
                                                PARTY ROW
                                            ================================== */}

                                            <tr>


                                                {/* # */}

                                                <td>
                                                    {index + 1}
                                                </td>


                                                {/* TYPE */}

                                                <td>

                                                    <span className="badge bg-secondary">

                                                        {party.partyTitle}

                                                    </span>

                                                </td>


                                                {/* PARTY NAME */}

                                                <td>

                                                    <strong>

                                                        {getPartyName(
                                                            party
                                                        )}

                                                    </strong>

                                                </td>


                                                {/* OPENING */}

                                                <td className="text-end fw-semibold">
    {formatCurrency(
        party.opening_balance || 0
    )}
</td>


                                                {/* LEDGER */}

                                                <td className="text-end fw-semibold">

                                                    {isBank ? (

                                                        <div>

                                                            <div>

                                                                USD:{" "}

                                                                {formatCurrency(
                                                                    party.credit_usd,
                                                                    "USD"
                                                                )}

                                                            </div>

                                                            <div>

                                                                BRL:{" "}

                                                                {formatCurrency(
                                                                    party.credit_brl,
                                                                    "BRL"
                                                                )}

                                                            </div>

                                                        </div>

                                                    ) : (

                                                        formatCurrency(
                                                            party.total_amount
                                                        )

                                                    )}

                                                </td>


                                                {/* TRANSACTIONS */}

                                                <td className="text-end">

                                                    {(
                                                        party.ledger_count ||
                                                        0
                                                    ).toLocaleString(
                                                        "en-IN"
                                                    )}

                                                </td>


                                                {/* BALANCE */}

                                                <td className="text-end">

                                                    {isBank ? (

                                                        <div>

                                                            <div
                                                                className={
                                                                    Number(
                                                                        party.usd_difference
                                                                    ) ===
                                                                    0
                                                                        ? "text-success"
                                                                        : "text-danger"
                                                                }
                                                            >

                                                                USD:{" "}

                                                                {formatCurrency(
                                                                    party.usd_difference,
                                                                    "USD"
                                                                )}

                                                            </div>

                                                            <div
                                                                className={
                                                                    Number(
                                                                        party.brl_difference
                                                                    ) ===
                                                                    0
                                                                        ? "text-success"
                                                                        : "text-danger"
                                                                }
                                                            >

                                                                BRL:{" "}

                                                                {formatCurrency(
                                                                    party.brl_difference,
                                                                    "BRL"
                                                                )}

                                                            </div>

                                                        </div>

                                                    ) : (

                                                        <span
                                                            className={
                                                                balance ===
                                                                0
                                                                    ? "text-success fw-bold"
                                                                    : "fw-bold"
                                                            }
                                                        >

                                                            {formatCurrency(
                                                                balance
                                                            )}

                                                        </span>

                                                    )}

                                                </td>


                                                {/* ACTION */}

                                                <td className="text-center">

                                                    <button
                                                        type="button"
                                                        className={`btn btn-sm ${
                                                            isExpanded
                                                                ? "btn-primary"
                                                                : "btn-outline-primary"
                                                        }`}
                                                        onClick={() =>
                                                            toggleParty(
                                                                party.partyKey,
                                                                partyId
                                                            )
                                                        }
                                                    >

                                                        {isExpanded ? (
                                                            <FaChevronUp />
                                                        ) : (
                                                            <FaChevronDown />
                                                        )}

                                                        <span className="ms-1">

                                                            {isExpanded
                                                                ? "Hide"
                                                                : "View"}

                                                        </span>

                                                    </button>

                                                </td>

                                            </tr>


                                            {/* ==================================
                                                EXPANDED LEDGER
                                            ================================== */}

                                            {isExpanded && (

                                                <tr>

                                                    <td
                                                        colSpan="8"
                                                        className="bg-light p-3"
                                                    >

                                                        <div className="card border-0 shadow-sm">


                                                            {/* LEDGER HEADER */}

                                                            <div className="card-header bg-dark text-white">

                                                                <strong>

                                                                    {getPartyName(
                                                                        party
                                                                    )}

                                                                    {" - "}

                                                                    Ledger Entries

                                                                </strong>

                                                            </div>


                                                            {/* LEDGER BODY */}

                                                            <div className="card-body p-0">


                                                                {party.ledger_entries &&
                                                                party.ledger_entries.length >
                                                                    0 ? (

                                                                    <div className="table-responsive">

                                                                        <table className="table table-sm table-bordered table-hover mb-0">

                                                                            <thead className="table-secondary">

                                                                                <tr>

                                                                                    <th>
                                                                                        #
                                                                                    </th>

                                                                                    <th>
                                                                                        Date
                                                                                    </th>

                                                                                    <th>
                                                                                        Type
                                                                                    </th>

                                                                                    <th>
                                                                                        Invoice
                                                                                    </th>

                                                                                    <th>
                                                                                        Booking
                                                                                    </th>

                                                                                    <th>
                                                                                        Material
                                                                                    </th>

                                                                                    <th>
                                                                                        Container
                                                                                    </th>

                                                                                    <th className="text-end">
                                                                                        Amount
                                                                                    </th>

                                                                                    <th>
                                                                                        Currency
                                                                                    </th>

                                                                                </tr>

                                                                            </thead>


                                                                            <tbody>

                                                                                {party.ledger_entries.map(
                                                                                    (
                                                                                        ledger,
                                                                                        ledgerIndex
                                                                                    ) => {

                                                                                        /*
                                                                                         * Determine amount.
                                                                                         *
                                                                                         * The backend has already
                                                                                         * calculated total_amount,
                                                                                         * but for individual rows
                                                                                         * we determine the correct
                                                                                         * field according to party.
                                                                                         */

                                                                                        let amount =
                                                                                            0;


                                                                                        switch (
                                                                                            party.partyKey
                                                                                        ) {

                                                                                            case "suppliers":

                                                                                                amount =
                                                                                                    Number(
                                                                                                        ledger.supplier_amount
                                                                                                    ) ||
                                                                                                    0;

                                                                                                break;


                                                                                            case "buyers":

                                                                                                amount =
                                                                                                    Number(
                                                                                                        ledger.amount_received
                                                                                                    ) ||
                                                                                                    0;

                                                                                                break;


                                                                                            case "redex_companies":

                                                                                                amount =
                                                                                                    Number(
                                                                                                        ledger.redex_amount
                                                                                                    ) ||
                                                                                                    0;

                                                                                                break;


                                                                                            case "despatchos":

                                                                                                amount =
                                                                                                    Number(
                                                                                                        ledger.despatcho_amount
                                                                                                    ) ||
                                                                                                    0;

                                                                                                break;


                                                                                            case "shippings":

                                                                                                amount =
                                                                                                    Number(
                                                                                                        ledger.shipping_line_amount
                                                                                                    ) ||
                                                                                                    0;

                                                                                                break;


                                                                                            case "transporters":

                                                                                                amount =
                                                                                                    Number(
                                                                                                        ledger.transportation_amount
                                                                                                    ) ||
                                                                                                    0;

                                                                                                break;


                                                                                            default:

                                                                                                amount =
                                                                                                    Number(
                                                                                                        ledger.party?.party_amount
                                                                                                    ) ||
                                                                                                    Number(
                                                                                                        ledger.final_amount
                                                                                                    ) ||
                                                                                                    0;

                                                                                        }


                                                                                        return (

                                                                                            <tr
                                                                                                key={
                                                                                                    ledger._id ||
                                                                                                    ledgerIndex
                                                                                                }
                                                                                            >

                                                                                                <td>

                                                                                                    {ledgerIndex +
                                                                                                        1}

                                                                                                </td>


                                                                                                <td>

                                                                                                    {ledger.date
                                                                                                        ? new Date(
                                                                                                            ledger.date
                                                                                                        ).toLocaleDateString(
                                                                                                            "en-IN"
                                                                                                        )
                                                                                                        : "N/A"}

                                                                                                </td>


                                                                                                <td>

                                                                                                    <span className="badge bg-secondary">

                                                                                                        {
                                                                                                            ledger.type ||
                                                                                                            "N/A"
                                                                                                        }

                                                                                                    </span>

                                                                                                </td>


                                                                                                <td>

                                                                                                    {
                                                                                                        ledger.invoice_no ||
                                                                                                        "N/A"
                                                                                                    }

                                                                                                </td>


                                                                                                <td>

                                                                                                    {
                                                                                                        ledger.booking_no ||
                                                                                                        "N/A"
                                                                                                    }

                                                                                                </td>


                                                                                                <td>

                                                                                                    {typeof ledger.material ===
                                                                                                    "object"
                                                                                                        ? ledger.material?.name ||
                                                                                                            "N/A"
                                                                                                        : ledger.material ||
                                                                                                            "N/A"}

                                                                                                </td>


                                                                                                <td>

                                                                                                    {
                                                                                                        ledger.container_no ||
                                                                                                        "N/A"
                                                                                                    }

                                                                                                </td>


                                                                                                <td className="text-end fw-semibold">

                                                                                                    {formatCurrency(
                                                                                                        amount
                                                                                                    )}

                                                                                                </td>


                                                                                                <td>

                                                                                                    {
                                                                                                        ledger.currency ||
                                                                                                        "N/A"
                                                                                                    }

                                                                                                </td>

                                                                                            </tr>

                                                                                        );

                                                                                    }
                                                                                )}

                                                                            </tbody>

                                                                        </table>

                                                                    </div>

                                                                ) : (

                                                                    <div className="text-center text-muted py-4">

                                                                        No ledger
                                                                        transactions
                                                                        found.

                                                                    </div>

                                                                )}

                                                            </div>

                                                        </div>

                                                    </td>

                                                </tr>

                                            )}

                                        </React.Fragment>

                                    );

                                }
                            )}

                        </tbody>

                    </table>

                </div>

            </div>

        </div>

    );

}


export default LedgerCrossCheck;